"use client";

import { useConsent } from "@/hooks/useConsent";
import { useEffect } from "react";
import { useTrackEvent } from "@/hooks/useTrackEvent";
import { isSafePublicTransactionId } from "@/lib/privacy/urlSanitization";

type PageViewTrackerProps = {
  event: string;
  properties?: Record<string, unknown>;
  dedupeKey?: string;
  includeAttribution?: boolean;
};

export function PageViewTracker({ event, properties, dedupeKey, includeAttribution = true }: PageViewTrackerProps) {
  const { consent } = useConsent();
  const trackEvent = useTrackEvent({ includeAttribution });
  const safeDedupeKey = isSafePublicTransactionId(dedupeKey) ? dedupeKey?.trim() : undefined;

  useEffect(() => {
    if (!consent.analytics) return;
    if (dedupeKey && !safeDedupeKey) return;
    if (safeDedupeKey) {
      const storageKey = `sa:analytics-once:${event}:${safeDedupeKey}`;
      try {
        if (window.sessionStorage.getItem(storageKey) === "1") return;
        window.sessionStorage.setItem(storageKey, "1");
      } catch {
        // Continue tracking if session storage is unavailable.
      }
    }
    trackEvent(event, {
      ...properties,
      ...(safeDedupeKey ? { $insert_id: safeDedupeKey } : {}),
    });
  }, [consent.analytics, dedupeKey, event, properties, safeDedupeKey, trackEvent]);

  return null;
}
